/**
 * BalanceCheckReport.js — Lighthouse-style balance analysis report.
 *
 * Displays an overall score circle, category badges, similar official
 * mechanics, and recommendations based on rules analysis.
 *
 * Part of the Homebrew Workshop (Issue #82, Phase F: #181)
 */

import React from 'react';
import {
  Scale,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  AlertCircle,
  ChevronRight,
} from 'lucide-react';

/**
 * Returns the Tailwind color class based on score thresholds.
 */
function scoreColorClass(score) {
  if (score >= 80) return 'text-green-400';
  if (score >= 50) return 'text-yellow-400';
  return 'text-red-400';
}

/**
 * Returns the status icon, color, and label for a category.
 */
function categoryStatus(score) {
  if (score >= 80) return { Icon: CheckCircle2, color: 'text-green-400', label: 'pass' };
  if (score >= 50) return { Icon: AlertTriangle, color: 'text-yellow-400', label: 'warn' };
  return { Icon: XCircle, color: 'text-red-400', label: 'fail' };
}

/**
 * Returns the Tailwind classes for a similarity badge pill.
 */
function similarityBadgeClass(similarity) {
  switch (similarity) {
    case 'high':
      return 'bg-green-400/20 text-green-400';
    case 'medium':
      return 'bg-yellow-400/20 text-yellow-400';
    default:
      return 'bg-gray-600/30 text-gray-400';
  }
}

/**
 * SVG arc circle showing the overall balance score.
 */
function ScoreCircle({ score }) {
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - score / 100);
  const color = scoreColorClass(score);

  return (
    <div className="flex justify-center">
      <svg
        width="64"
        height="64"
        viewBox="0 0 64 64"
        aria-label={`Balance score: ${score} out of 100`}
      >
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          stroke="currentColor"
          className="text-gray-700"
          strokeWidth="4"
        />
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          stroke="currentColor"
          className={color}
          strokeWidth="4"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 32 32)"
        />
        <text
          x="32"
          y="32"
          textAnchor="middle"
          dominantBaseline="central"
          className={`fill-current ${color} text-lg font-bold`}
        >
          {score}
        </text>
      </svg>
    </div>
  );
}

/**
 * @param {object} props
 * @param {object|null} props.report - Balance check report data
 * @param {boolean} props.loading - Whether a check is in progress
 * @param {string|null} props.error - Error message if check failed
 * @param {boolean} props.stale - Whether the mechanic changed since last check
 * @param {number} props.creditsRemaining - Remaining daily balance checks
 * @param {function} props.onRunCheck - Callback to trigger a balance check
 */
export default function BalanceCheckReport({
  report,
  loading,
  error,
  stale,
  creditsRemaining,
  onRunCheck,
}) {
  const header = (
    <h3 className="text-sm font-bold text-gray-400 uppercase mb-2">Balance Check</h3>
  );

  // --- Loading State ---
  if (loading) {
    return (
      <div className="mt-4">
        {header}
        <div className="space-y-3">
          <div className="w-16 h-16 rounded-full bg-gray-700 animate-pulse mx-auto" />
          <div className="space-y-1.5">
            <div className="h-4 bg-gray-700 rounded animate-pulse w-full" />
            <div className="h-4 bg-gray-700 rounded animate-pulse w-[85%]" />
            <div className="h-4 bg-gray-700 rounded animate-pulse w-[90%]" />
            <div className="h-4 bg-gray-700 rounded animate-pulse w-[75%]" />
            <div className="h-4 bg-gray-700 rounded animate-pulse w-[95%]" />
          </div>
          <p className="text-xs text-gray-500">Analyzing...</p>
        </div>
      </div>
    );
  }

  // --- Error State ---
  if (error) {
    return (
      <div className="mt-4">
        {header}
        <div className="flex items-start gap-1.5 text-xs text-red-400">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
        <button
          onClick={onRunCheck}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-gray-800 text-gray-300 border border-gray-600 hover:border-gray-500 hover:text-white transition-colors mt-2"
        >
          Retry
        </button>
      </div>
    );
  }

  // --- Report State ---
  if (report) {
    const categories = report.categories || [];
    const similarMechanics = (report.similarMechanics || []).slice(0, 3);
    const recommendations = report.recommendations || [];

    return (
      <div className="mt-4">
        {header}

        {/* Stale banner */}
        {stale && (
          <div className="flex items-center justify-between gap-2 px-2 py-1 bg-yellow-900/30 border border-yellow-700/50 rounded text-xs text-yellow-400 mb-2">
            <span>Mechanic changed since last check</span>
            <button
              onClick={onRunCheck}
              className="text-yellow-400 hover:text-yellow-300 underline"
            >
              Re-check
            </button>
          </div>
        )}

        {/* Overall Score */}
        <ScoreCircle score={report.overallScore ?? 0} />

        {/* Category Badges */}
        <div className="space-y-1 mt-3">
          {categories.map((cat) => {
            const { Icon, color } = categoryStatus(cat.score);
            return (
              <div key={cat.name}>
                <div className="flex items-center gap-2 text-xs">
                  <Icon className={`w-3.5 h-3.5 shrink-0 ${color}`} />
                  <span className={color}>
                    {cat.name}: {cat.score}/100
                  </span>
                </div>
                {cat.detail && (
                  <p className="text-xs text-gray-500 pl-5">{cat.detail}</p>
                )}
              </div>
            );
          })}
        </div>

        {/* Similar Official Mechanics */}
        {similarMechanics.length > 0 && (
          <div className="mt-3">
            <h4 className="text-xs font-semibold text-gray-400 uppercase mb-1">
              Similar Official Mechanics
            </h4>
            <div className="space-y-1.5">
              {similarMechanics.map((mech, i) => (
                <div key={mech.name || i}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-300">{mech.name}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full ${similarityBadgeClass(
                        mech.similarity
                      )}`}
                    >
                      {mech.similarity}
                    </span>
                  </div>
                  {mech.comparison && (
                    <p className="text-xs text-gray-500">{mech.comparison}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div className="mt-3">
            <h4 className="text-xs font-semibold text-gray-400 uppercase mb-1">
              Recommendations
            </h4>
            <div className="space-y-1">
              {recommendations.map((rec, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs text-gray-300">
                  <ChevronRight className="w-3 h-3 mt-0.5 shrink-0 text-gray-500" />
                  <span>{rec}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Empty State ---
  return (
    <div className="mt-4">
      {header}
      <p className="text-xs text-gray-500 mb-3">
        Analyze your mechanic against official rules for balance feedback.
      </p>
      <button
        onClick={onRunCheck}
        disabled={creditsRemaining === 0}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-gray-800 text-gray-300 border border-gray-600 hover:border-gray-500 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-600 disabled:hover:text-gray-300"
      >
        <Scale className="w-4 h-4" />
        Check Balance
      </button>
      {creditsRemaining === 0 && (
        <p className="text-xs text-gray-500 mt-1">Daily limit reached</p>
      )}
    </div>
  );
}
